/**
 * Générateur — Il modélise un phénomène continu par une fonction.
 * Attendu ID : 228 | Niveau : 3ème | Domaine : Fonctions (d13, O16)
 *
 * Source doc prof (B3S3). Plan auto-validé 2026-07-16 (TODO §6) :
 * « programme » (programme de calcul → fonction réduite, image, antécédent),
 * « geometrie » (aire dépendant d'une longueur : encadrement + f(x) + image),
 * « resistance » (R(v) = kv² : image + antécédent, solution négative rejetée
 * par le CONTEXTE — à faire remarquer).
 */

import { randomChoice, randInt, tex, makeTexteTrou } from '../../../utils';
import { NOMS_FN } from './fonctions';

const ATTENDU_ID = 228;
const L_PROGRAMME = 'Du programme de calcul à la fonction';
const L_GEOMETRIE = 'Modéliser une aire par une fonction';
const L_RESISTANCE = 'Un modèle en v² : image et antécédent en contexte';
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ═════════ « programme » ═════════

const genProgrammeTrous = () => {
    const nom = randomChoice(NOMS_FN);
    // « ajoute p, multiplie par k, soustrais s » → kx + (kp − s)
    const p = randInt(1, 6); const k = randInt(2, 5); const s = randInt(1, 9);
    const b = k * p - s;
    if (b === 0) return genProgrammeTrous.repli(nom);
    const cible = k * randInt(2, 9) + b;                   // antécédent ENTIER par construction
    const ant = (cible - b) / k;
    const phrase = `$${nom}(x) = {{${k}}}x ${b > 0 ? '+' : '-'} {{${Math.abs(b)}}} \\qquad`
        + ` ${nom}(0) = {{${b}}} \\qquad \\text{pour obtenir } ${cible} \\text{, il faut choisir } {{${ant}}}$`;
    const payload = makeTexteTrou(phrase,
        `<p>Programme : choisis un nombre x ; ajoute ${p} ; multiplie le résultat par ${k} ; soustrais ${s}.</p>`
        + `<p><b>Réduis l'expression de ${nom}(x), puis calcule ${nom}(0) et le nombre de départ qui donne ${cible}.</b></p>`,
        { ...opts(L_PROGRAMME), mode: 'saisie' });
    payload._meta.params = { nom, p, k, s, b, cible, ant };
    return payload;
};
genProgrammeTrous.repli = (nom) => {
    const payload = makeTexteTrou(
        `$${nom}(x) = {{3}}x + {{9}} \\qquad ${nom}(0) = {{9}}`
        + ' \\qquad \\text{pour obtenir } 18 \\text{, il faut choisir } {{3}}$',
        '<p>Programme : choisis un nombre x ; ajoute 5 ; multiplie le résultat par 3 ; soustrais 6.</p>'
        + `<p><b>Réduis l'expression de ${nom}(x), puis calcule ${nom}(0) et le nombre de départ qui donne 18.</b></p>`,
        { ...opts(L_PROGRAMME), mode: 'saisie' });
    payload._meta.params = { repli: true };
    return payload;
};

// ═════════ « geometrie » — aire = fonction d'une longueur ═════════

const genGeometrieTrous = () => {
    const nom = randomChoice(NOMS_FN);
    // Triangle AMC : base MC = x sur [DC] (rectangle L × h) → aire = (h/2)·x.
    const h = randomChoice([4, 6, 8, 10]);                 // h pair → h/2 entier
    const L = randomChoice([9, 12, 14, 16, 18]);
    const demiH = h / 2;
    const v = randInt(2, L - 1);
    const phrase = `$\\text{x varie entre 0 et } {{${L}}} \\qquad ${nom}(x) = {{${demiH}}}x`
        + ` \\qquad ${nom}(${v}) = {{${demiH * v}}}\\ \\text{cm}^{2}$`;
    const payload = makeTexteTrou(phrase,
        `<p>ABCD est un rectangle avec ${tex(`AB = ${L}`)} cm et ${tex(`AD = ${h}`)} cm. M est un `
        + `point de ${tex('\\lbrack DC\\rbrack')} et on pose ${tex('MC = x')}. L'aire du triangle AMC `
        + `vaut ${tex('\\dfrac{MC \\times AD}{2}')} : on la note ${tex(`${nom}(x)`)}.</p>`
        + `<p><b>Donne l'encadrement de x, l'expression de ${nom}(x), puis l'aire pour MC = ${v} cm.</b></p>`,
        { ...opts(L_GEOMETRIE), mode: 'saisie' });
    payload._meta.params = { nom, h, L, v };
    return payload;
};

// ═════════ « resistance » — kv², contexte qui rejette le négatif ═════════

const genResistanceTrous = () => {
    // R(v) = k·v² (k entier simple) : R(v0), puis antécédent de k·c².
    const k = randomChoice([2, 3, 4, 5]);
    const v0 = randomChoice([10, 20, 30, 40, 50]);
    const c = randomChoice([60, 70, 80, 90, 100, 110, 120, 130].filter(x => x !== v0));
    const cible = k * c * c;
    const phrase = `$R(${v0}) = {{${k * v0 * v0}}}\\ \\text{N} \\qquad`
        + ` \\text{la vitesse cherchée est } {{${c}}}\\ \\text{km/h}$`;
    const payload = makeTexteTrou(phrase,
        `<p>La résistance de l'air sur un véhicule est modélisée par ${tex(`R(v) = ${k}v^{2}`)}, `
        + `où R est en newtons et v en km/h, avec ${tex('v \\geqslant 0')}.</p>`
        // « résous v² = … ; la solution négative est REJETÉE » donnait l'équation
        // ET la discussion, qui EST l'objet de la carte.
        + `<p><b>Calcule ${tex(`R(${v0})`)}, puis la vitesse pour laquelle R vaut ${cible} N.</b></p>`,
        { ...opts(L_RESISTANCE), mode: 'saisie' });
    payload._meta.params = { k, v0, c, cible };
    return payload;
};

const GENERATORS = {
    programme_texte_trou_saisie: genProgrammeTrous,
    geometrie_texte_trou_saisie: genGeometrieTrous,
    resistance_texte_trou_saisie: genResistanceTrous,
};
const FORMAT_LABELS = { texte_trou_saisie: 'Saisie trous' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));
export const CATEGORIES = [
    { id: 'programme', label: L_PROGRAMME, formatsList: buildFormats('programme') },
    { id: 'geometrie', label: L_GEOMETRIE, formatsList: buildFormats('geometrie') },
    { id: 'resistance', label: L_RESISTANCE, formatsList: buildFormats('resistance') },
];
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
